import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Search, 
  Edit, 
  Trash2, 
  Plus,
  Calendar,
  Globe,
  Save,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { groundsService } from '../services/supabaseService';
import PageLayout from './PageLayout';
import '../styles/ViewGrounds.css';

const ViewGrounds = () => {
  const [grounds, setGrounds] = useState([]);
  const [filteredGrounds, setFilteredGrounds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groundToDelete, setGroundToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useAuth(); // Keep the hook call for context functionality
  const navigate = useNavigate();

  // Show notification function
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch grounds from Supabase
  useEffect(() => {
    const fetchGrounds = async () => {
      try {
        setLoading(true);
        const data = await groundsService.getAll();
        setGrounds(data);
        setFilteredGrounds(data);
      } catch (err) {
        console.error('Error fetching grounds:', err);
        setError('Failed to load grounds. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchGrounds();
  }, []);

  // Filter logic
  useEffect(() => {
    let filtered = grounds;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(ground =>
        ground.ground_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ground.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ground.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ground.address_1.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ground.country.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredGrounds(filtered);
  }, [grounds, searchTerm]);

  const handleEdit = (ground) => {
    setEditingId(ground.id);
    setEditForm({
      ground_name: ground.ground_name,
      address_1: ground.address_1,
      address_2: ground.address_2 || '',
      city: ground.city,
      state: ground.state,
      zip_code: ground.zip_code,
      country: ground.country
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (groundId) => {
    try {
      setUpdateLoading(true);
      
      // Transform data to match the service expected format
      const updateData = {
        groundName: editForm.ground_name,
        address1: editForm.address_1,
        address2: editForm.address_2,
        city: editForm.city,
        state: editForm.state,
        zipCode: editForm.zip_code,
        country: editForm.country
      };
      
      // Update in Supabase
      await groundsService.update(groundId, updateData);
      
      // Update local state with the original format
      const updatedGrounds = grounds.map(ground => 
        ground.id === groundId ? { ...ground, ...editForm } : ground
      );
      
      setGrounds(updatedGrounds);
      setEditingId(null);
      setEditForm({});
      
      // Show success message
      showNotification('Ground updated successfully!', 'success');
      setError('');
      
    } catch (err) {
      console.error('Error updating ground:', err);
      showNotification('Failed to update ground. Please try again.', 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDelete = (ground) => {
    setGroundToDelete(ground);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!groundToDelete) return;

    try {
      setIsDeleting(true);
      await groundsService.delete(groundToDelete.id);
      setGrounds(grounds.filter(g => g.id !== groundToDelete.id));
      showNotification(`Ground "${groundToDelete.ground_name}" has been deleted successfully!`, 'success');
      setShowDeleteModal(false);
      setGroundToDelete(null);
    } catch (err) {
      console.error('Error deleting ground:', err);
      showNotification('Failed to delete ground. Please try again.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setGroundToDelete(null);
  };

  const formatAddress = (ground) => {
    const parts = [ground.address_1];
    if (ground.address_2) parts.push(ground.address_2);
    parts.push(`${ground.city}, ${ground.state} ${ground.zip_code}`);
    return parts.join(', ');
  };

  const renderGroundCard = (ground) => {
    const isEditing = editingId === ground.id;

    return (
      <div key={ground.id} className="vg-ground-card">
        <div className="vg-card-content">
          <div className="vg-card-header">
            {isEditing ? (
              <input
                type="text"
                value={editForm.ground_name}
                onChange={(e) => handleInputChange('ground_name', e.target.value)}
                className="vg-edit-input vg-edit-title"
                placeholder="Ground Name"
              />
            ) : (
              <h3 className="vg-ground-name">{ground.ground_name}</h3>
            )}
          </div>

          <div className="vg-ground-details">
            {isEditing ? (
              <>
                <div className="vg-edit-row">
                  <MapPin size={16} />
                  <input
                    type="text"
                    value={editForm.address_1}
                    onChange={(e) => handleInputChange('address_1', e.target.value)}
                    className="vg-edit-input"
                    placeholder="Address Line 1"
                  />
                </div>
                <div className="vg-edit-row">
                  <div className="vg-edit-spacer"></div>
                  <input
                    type="text"
                    value={editForm.address_2}
                    onChange={(e) => handleInputChange('address_2', e.target.value)}
                    className="vg-edit-input"
                    placeholder="Address Line 2 (Optional)"
                  />
                </div>
                <div className="vg-edit-row-group">
                  <div className="vg-edit-row">
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="vg-edit-input vg-edit-small"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      value={editForm.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="vg-edit-input vg-edit-small"
                      placeholder="State"
                    />
                    <input
                      type="text"
                      value={editForm.zip_code}
                      onChange={(e) => handleInputChange('zip_code', e.target.value)}
                      className="vg-edit-input vg-edit-small"
                      placeholder="ZIP"
                    />
                  </div>
                </div>
                <div className="vg-edit-row">
                  <Globe size={16} />
                  <input
                    type="text"
                    value={editForm.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="vg-edit-input"
                    placeholder="Country"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="vg-detail-item">
                  <MapPin size={16} />
                  <span>{formatAddress(ground)}</span>
                </div>
                <div className="vg-detail-item">
                  <Globe size={16} />
                  <span>{ground.country}</span>
                </div>
              </>
            )}
            <div className="vg-detail-item">
              <Calendar size={16} />
              <span>Created: {new Date(ground.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="vg-card-actions">
            {isEditing ? (
              <>
                <button 
                  className="vg-action-btn vg-save-btn"
                  onClick={() => handleSaveEdit(ground.id)}
                  disabled={updateLoading}
                >
                  {updateLoading ? (
                    <div className="vg-loading-spinner-small"></div>
                  ) : (
                    <Save size={16} />
                  )}
                  <span>{updateLoading ? 'Saving...' : 'Save'}</span>
                </button>
                <button 
                  className="vg-action-btn vg-cancel-btn"
                  onClick={handleCancelEdit}
                  disabled={updateLoading}
                >
                  <X size={16} />
                  <span>Cancel</span>
                </button>
              </>
            ) : (
              <>
                <button 
                  className="vg-action-btn vg-edit-btn"
                  onClick={() => handleEdit(ground)}
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button 
                  className="vg-action-btn vg-delete-btn"
                  onClick={() => handleDelete(ground)}
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageLayout className="view-grounds-page">
      {/* Toast Notification */}
      {notification && (
        <div className={`vg-toast-notification ${notification.type}`}>
          {notification.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <XCircle size={20} />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="cg-header">
        <div className="cg-header-left">
          <h1 className="cg-title">Venues Management</h1>
          <p className="cg-subtitle">View and manage all your sports venues</p>
        </div>
        <div className="vg-header-right">
          <button 
            className="vg-create-btn" 
            onClick={() => navigate('/CreateGround')}
          >
            <Plus size={20} />
            <span>Create New Venue</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="vg-controls">
        <div className="vg-search-container">
          <Search size={20} className="vg-search-icon" />
          <input
            type="text"
            placeholder="Search grounds by name, city, state, address, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="vg-search-input"
          />
        </div>
      </div>

      {/* Results Count */}
      <div className="vg-results-info">
        <span className="vg-results-count">
          Showing {filteredGrounds.length} of {grounds.length} venues
        </span>
      </div>

      {/* Error Message */}
      {error && (
        <div className="vg-error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Grounds Grid */}
      {loading ? (
        <div className="vg-loading">
          <div className="vg-loading-spinner"></div>
          <p>Loading venues...</p>
        </div>
      ) : filteredGrounds.length === 0 ? (
        <div className="vg-empty-state">
          <MapPin size={64} className="vg-empty-icon" />
          <h3>{searchTerm ? 'No grounds found' : 'No grounds yet'}</h3>
          <p>
            {searchTerm 
              ? 'Try adjusting your search criteria' 
              : 'Create your first ground to get started'
            }
          </p>
          <button 
            className="vg-create-btn" 
            onClick={() => navigate('/CreateGround')}
          >
            <Plus size={20} />
            <span>Create Your First Venue</span>
          </button>
        </div>
      ) : (
        <div className="vg-grounds-grid">
          {filteredGrounds.map(renderGroundCard)}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && groundToDelete && (
        <div className="vg-modal-overlay">
          <div className="vg-confirm-modal">
            <div className="vg-modal-header">
              <AlertTriangle size={24} className="vg-warning-icon" />
              <h3>Confirm Deletion</h3>
            </div>
            
            <div className="vg-modal-content">
              <p>
                Are you sure you want to delete the venue 
                <strong> "{groundToDelete.ground_name}"</strong>?
              </p>
              <div className="vg-ground-details-modal">
                <p>• Location: {groundToDelete.city}, {groundToDelete.state}</p>
                <p>• Address: {formatAddress(groundToDelete)}</p>
              </div>
              <p className="vg-warning-text">
                This action cannot be undone. All venue data will be permanently removed.
              </p>
            </div>
            
            <div className="vg-modal-actions">
              <button
                className="vg-modal-cancel-btn"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="vg-modal-delete-btn"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Ground'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ViewGrounds;